<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const tablesStore = useTablesStore();

const tables = computed(() => tablesStore.list);

const isCreating = ref(false);
const newTableName = ref('');
const renamingId = ref(null);
const renameValue = ref('');
const errorMessage = ref('');

const openTable = id => {
  router.push({
    name: 'tables_detail',
    params: { accountId: route.params.accountId, tableId: id },
  });
};

const submitNewTable = async () => {
  if (!newTableName.value.trim()) return;
  errorMessage.value = '';
  try {
    const table = await tablesStore.createTable({ name: newTableName.value.trim() });
    newTableName.value = '';
    isCreating.value = false;
    openTable(table.id);
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || t('TABLES.ERROR');
  }
};

const startRename = table => {
  renamingId.value = table.id;
  renameValue.value = table.name;
};

const submitRename = async () => {
  if (!renameValue.value.trim()) return;
  await tablesStore.updateTable(renamingId.value, { name: renameValue.value.trim() });
  renamingId.value = null;
};

const removeTable = async table => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TABLES.CONFIRM_DELETE', { name: table.name }))) return;
  await tablesStore.deleteTable(table.id);
};

onMounted(() => tablesStore.fetch());
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <h1 class="m-0 text-lg font-semibold text-n-slate-12">
        {{ t('TABLES.TITLE') }}
      </h1>
      <Button
        :label="t('TABLES.NEW_TABLE')"
        icon="i-lucide-plus"
        size="sm"
        @click="isCreating = !isCreating"
      />
    </div>

    <div v-if="isCreating" class="flex items-center gap-2 px-6 py-3 border-b border-n-weak">
      <Input
        v-model="newTableName"
        :placeholder="t('TABLES.NAME_PLACEHOLDER')"
        class="w-72"
        @keyup.enter="submitNewTable"
      />
      <Button :label="t('TABLES.CREATE')" size="sm" @click="submitNewTable" />
      <span v-if="errorMessage" class="text-sm text-n-ruby-11">{{ errorMessage }}</span>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <p v-if="!tables.length" class="text-sm text-n-slate-11">
        {{ t('TABLES.EMPTY') }}
      </p>

      <ul v-else class="grid gap-3 m-0 list-none md:grid-cols-2 xl:grid-cols-3">
        <li
          v-for="table in tables"
          :key="table.id"
          class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak bg-n-solid-1"
        >
          <div v-if="renamingId === table.id" class="flex items-center gap-2">
            <Input v-model="renameValue" class="flex-1" @keyup.enter="submitRename" />
            <Button :label="t('TABLES.SAVE')" size="xs" @click="submitRename" />
          </div>
          <template v-else>
            <button
              class="text-base font-medium text-left text-n-slate-12 hover:underline"
              @click="openTable(table.id)"
            >
              {{ table.name }}
            </button>
            <!-- The slug is the machine-facing identity a bot names this
                 table by, so it is shown alongside the display name. -->
            <code class="text-xs text-n-slate-11">{{ table.slug }}</code>
            <p v-if="table.description" class="m-0 text-sm text-n-slate-11">
              {{ table.description }}
            </p>
            <div class="flex gap-2 mt-2">
              <Button
                :label="t('TABLES.OPEN')"
                size="xs"
                variant="faded"
                @click="openTable(table.id)"
              />
              <Button
                :label="t('TABLES.RENAME')"
                size="xs"
                variant="faded"
                @click="startRename(table)"
              />
              <Button
                :label="t('TABLES.DELETE')"
                size="xs"
                color="ruby"
                variant="faded"
                @click="removeTable(table)"
              />
            </div>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>
